/**
 * Runtime language registry.
 *
 * The supported-language list used to be the compile-time constant
 * ``SUPPORTED_LANGS = ["cs", "en"]``. It is now DB-backed
 * (:class:`PlatformLanguage`) and delivered on the public branding
 * payload as ``available_languages``. This module holds the *runtime*
 * set so the ``/:lang`` router gate, the language switcher, the SEO
 * hreflang alternates and the admin per-locale editors all read one
 * source that updates when branding resolves — without threading the
 * list through props.
 *
 * Two-tier model:
 *
 * * **Builtin** (``cs`` / ``en``) — always gate-valid regardless of the
 *   DB state, because they ship compiled FE bundles + BE templates and
 *   are the i18next ``fallbackLng``. Disabling a builtin only hides it
 *   from the switcher; a ``/cs`` URL never hard-fails.
 * * **Active** — the *enabled* set from the branding payload (what the
 *   switcher shows). Seeded from ``localStorage`` at boot so a repeat
 *   visitor deep-linking to an admin-added locale (``/de/...``) passes
 *   the gate on first paint, before branding resolves. A first-ever
 *   visit to an unknown locale still redirects to the default and
 *   self-heals once branding caches the list.
 */

import { useSyncExternalStore } from "react";

export type LanguageSource = "builtin" | "custom";

export interface ActiveLanguage {
  code: string;
  label: string;
  isDefault: boolean;
  source: LanguageSource;
}

/** Compiled-in languages — always valid for routing + always a fallback. */
export const BUILTIN_LANGUAGES: readonly ActiveLanguage[] = [
  { code: "cs", label: "Čeština", isDefault: true, source: "builtin" },
  { code: "en", label: "English", isDefault: false, source: "builtin" },
];

const BUILTIN_CODES: ReadonlySet<string> = new Set(
  BUILTIN_LANGUAGES.map((l) => l.code),
);

const LS_KEY = "forgmatic.i18n.languages.v1";

function readPersisted(): ActiveLanguage[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const clean = parsed.filter(
      (l): l is ActiveLanguage =>
        !!l && typeof l.code === "string" && typeof l.label === "string",
    );
    return clean.length ? clean : null;
  } catch {
    return null;
  }
}

function persist(list: ActiveLanguage[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* private-mode / quota — the in-memory registry still works. */
  }
}

// Snapshot reference is swapped wholesale on update so ``useSyncExternalStore``
// sees referential equality between renders (no infinite re-render loop).
let active: ActiveLanguage[] = readPersisted() ?? [...BUILTIN_LANGUAGES];

const listeners = new Set<() => void>();

function emit(): void {
  for (const cb of listeners) cb();
}

/** The enabled languages, in admin sort order — what the switcher renders. */
export function getActiveLanguages(): ActiveLanguage[] {
  return active;
}

export function getActiveLangCodes(): string[] {
  return active.map((l) => l.code);
}

/** The default locale (``/`` redirect target). Falls back to ``cs``. */
export function getRuntimeDefaultLang(): string {
  return active.find((l) => l.isDefault)?.code ?? "cs";
}

/**
 * Is ``value`` a routable language code? True for any enabled language OR
 * any builtin (builtins stay valid even if an admin disabled them, so a
 * ``/cs`` link never 404s and the fallback locale always resolves).
 */
export function isActiveLang(value: unknown): value is string {
  if (typeof value !== "string") return false;
  return BUILTIN_CODES.has(value) || active.some((l) => l.code === value);
}

/**
 * Replace the active set (called when the branding payload resolves).
 * Empty / malformed input is ignored so a transient bad response can't
 * strand the switcher with zero languages.
 */
export function setActiveLanguages(list: ActiveLanguage[]): void {
  const clean = list.filter((l) => l && typeof l.code === "string" && l.code);
  if (!clean.length) return;
  // No-op if nothing changed — avoids a spurious re-render/persist.
  if (
    clean.length === active.length &&
    clean.every((l, i) => {
      const cur = active[i];
      return (
        cur &&
        cur.code === l.code &&
        cur.label === l.label &&
        cur.isDefault === l.isDefault &&
        cur.source === l.source
      );
    })
  ) {
    return;
  }
  active = clean;
  persist(clean);
  emit();
}

export function subscribeLanguages(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * React binding — re-renders the consumer when the active set changes.
 * ``getActiveLanguages`` returns the stable snapshot reference so
 * ``useSyncExternalStore`` doesn't loop.
 */
export function useActiveLanguages(): ActiveLanguage[] {
  return useSyncExternalStore(
    subscribeLanguages,
    getActiveLanguages,
    getActiveLanguages,
  );
}
